import React, { useState, useEffect } from 'react';
import { fetchPendingCDApprovals, reviewCDApproval } from '../../services/medicationService';
import { useAuth } from '../../context/AuthContext';

const PendingCDApprovalsTab = ({ serverId }) => {
    const { user, userDetails } = useAuth();
    const [approvals, setApprovals] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Modal State
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        loadApprovals();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverId]);

    const loadApprovals = async () => {
        try {
            setIsLoading(true);
            const data = await fetchPendingCDApprovals(serverId);
            setApprovals(data);
        } catch (err) {
            console.error("Failed to load CD approvals", err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReview = async (action) => {
        if (!selectedRequest) return;
        
        try {
            setIsSubmitting(true);
            const adminId = user?.uid;
            const adminName = userDetails?.firstName ? `${userDetails.firstName} ${userDetails.lastName}` : user?.email;
            
            await reviewCDApproval(
                serverId, 
                selectedRequest.id, 
                action, 
                comment, 
                adminId, 
                adminName
            );
            
            // Reload approvals and close modal
            await loadApprovals();
            closeModal();
        } catch (err) {
            console.error(`Failed to ${action} CD approval`, err);
            alert(`Failed to complete action: ${err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeModal = () => {
        setSelectedRequest(null);
        setComment('');
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', alignItems: 'center' }}>
                <h2 style={{ fontSize: '20px', color: '#2d3748', margin: 0 }}>Pending Controlled Drugs Approvals</h2>
                <button 
                    onClick={loadApprovals}
                    style={{ padding: '8px 16px', backgroundColor: '#edf2f7', color: '#4a5568', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                >
                    Refresh
                </button>
            </div>

            {isLoading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>Loading pending approvals...</div>
            ) : approvals.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f7fafc', borderRadius: '8px', color: '#718096' }}>
                    No pending CD approvals found.
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {approvals.map(req => (
                        <div key={req.id} style={{ 
                            padding: '16px', 
                            backgroundColor: 'white', 
                            borderRadius: '8px', 
                            border: '1px solid #e2e8f0',
                            borderLeft: `4px solid ${req.status === 'queried' ? '#dd6b20' : '#3182ce'}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div>
                                <div style={{ fontWeight: 'bold', color: '#2d3748', marginBottom: '4px' }}>
                                    {req.patientName || req.userID} - {req.medicationName}
                                </div>
                                <div style={{ fontSize: '14px', color: '#4a5568' }}>
                                    Requested by: {req.requestedByName || req.requestedBy}
                                </div>
                                {req.status === 'queried' && (
                                    <div style={{ fontSize: '12px', color: '#dd6b20', marginTop: '4px', fontWeight: 'bold' }}>
                                        Status: QUERIED
                                    </div>
                                )}
                            </div>
                            <div>
                                <button 
                                    onClick={() => setSelectedRequest(req)}
                                    style={{ padding: '8px 16px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                                >
                                    Review
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Review Modal */}
            {selectedRequest && (
                <div style={{ 
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', 
                    justifyContent: 'center', alignItems: 'center', zIndex: 1000 
                }}>
                    <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', width: '500px', maxWidth: '90%' }}>
                        <h3 style={{ margin: '0 0 15px 0', color: '#2d3748' }}>Review CD Approval</h3>
                        <p style={{ margin: '0 0 20px 0', color: '#718096' }}>
                            You are reviewing a request for <strong>{selectedRequest.medicationName}</strong> for <strong>{selectedRequest.patientName || selectedRequest.userID}</strong>.
                        </p>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', fontSize: '14px', fontWeight: 'bold', color: '#4a5568', marginBottom: '8px' }}>Comments / Query Details</label>
                            <textarea 
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Optional comments for approval/rejection or mandatory for query..."
                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0', minHeight: '80px', fontFamily: 'inherit' }}
                            />
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={closeModal}
                                disabled={isSubmitting}
                                style={{ padding: '10px 16px', backgroundColor: 'transparent', color: '#718096', border: 'none', cursor: 'pointer' }}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={() => handleReview('query')}
                                disabled={isSubmitting || !comment.trim()}
                                style={{ padding: '10px 16px', backgroundColor: '#edf2f7', color: '#dd6b20', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                                title="Send a query back to the requester"
                            >
                                Query
                            </button>
                            <button 
                                onClick={() => handleReview('reject')}
                                disabled={isSubmitting}
                                style={{ padding: '10px 16px', backgroundColor: '#fed7d7', color: '#c53030', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                            >
                                Reject
                            </button>
                            <button 
                                onClick={() => handleReview('override')}
                                disabled={isSubmitting}
                                style={{ padding: '10px 16px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
                                title="Approve immediately as Super Admin without requiring double signature"
                            >
                                Admin Override
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PendingCDApprovalsTab;
